import { Box, CircularProgress, Dialog, Typography } from '@mui/material';
import { Download, X } from 'lucide-react';

import { cx } from '@/styles/muiTheme';

import useClipDownloadMutation from '../../hooks/useClipDownloadMutation';
import useClipPlaybackQuery from '../../hooks/useClipPlaybackQuery';
import type { ClipLibraryItem } from '../../services/toClipLibraryItems';
import { ClipStatus } from '../../types';
import TeamCrest from './TeamCrest';

type Props = {
  /**
   * The clip to play. `null` closes the dialog. Only the fields read here, so
   * callers outside the library (the automation activity log) can open it too.
   */
  clip: Pick<
    ClipLibraryItem,
    | 'id'
    | 'status'
    | 'title'
    | 'gameLabel'
    | 'meta'
    | 'teamAbbreviation'
    | 'teamLogoUrl'
    | 'teamColor'
    | 'thumbnailUrl'
  > | null;
  onClose: () => void;
};

/**
 * Full-clip player, opened from a card in the library.
 *
 * Plays the exported master rather than the card's hover preview — see
 * `useClipPlaybackQuery`. Only exported clips have a file at all, so anything
 * short of READY is told so instead of being handed a player that would 404.
 *
 * Returns null when there is no clip, so closing unmounts the dialog outright
 * instead of fading out. That is what guarantees the <video> is torn down and
 * the audio stops; MUI would otherwise keep it playing through the transition.
 */
const ClipPlayerDialog = ({ clip, onClose }: Props) => {
  const isReady = clip?.status?.toUpperCase() === ClipStatus.READY;

  const playback = useClipPlaybackQuery(clip && isReady ? clip.id : null);
  const downloadClip = useClipDownloadMutation();

  if (!clip) return null;

  const handleDownload = () =>
    downloadClip.mutate({ clipId: clip.id, video: true, source: 'clip_library' });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px 16px 12px' }}>
        <TeamCrest
          abbreviation={clip.teamAbbreviation}
          logoUrl={clip.teamLogoUrl}
          color={clip.teamColor}
          size={28}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, lineHeight: '22px', color: cx.text.primary }}>
            {clip.title}
          </Typography>
          <Typography sx={{ fontSize: 12, lineHeight: '18px', color: cx.text.secondary }}>
            {[clip.gameLabel, clip.meta].filter(Boolean).join(' · ')}
          </Typography>
        </Box>

        <Box
          component="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            flexShrink: 0,
            border: 'none',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: cx.text.tertiary,
            cursor: 'pointer',
            '&:hover': { backgroundColor: cx.bg.elevated, color: cx.text.primary },
          }}
        >
          <X size={16} />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!isReady ? (
          <Typography sx={{ fontSize: 13, color: cx.text.tertiary, textAlign: 'center', padding: '24px' }}>
            This clip is {clip.status.toLowerCase()} — it can be played once the export finishes.
          </Typography>
        ) : playback.isPending ? (
          <CircularProgress size={28} />
        ) : playback.isError ? (
          <Typography sx={{ fontSize: 13, color: cx.red.main, textAlign: 'center', padding: '24px' }}>
            {playback.error.message || 'This clip could not be loaded for playback.'}
          </Typography>
        ) : (
          <Box
            component="video"
            // Remounts when the URL changes, so switching clips never leaves the
            // previous source buffered behind the new poster.
            key={playback.data}
            src={playback.data}
            poster={clip.thumbnailUrl ?? undefined}
            controls
            autoPlay
            playsInline
            sx={{ width: '100%', height: '100%', display: 'block' }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '12px 16px 16px',
        }}
      >
        <Typography sx={{ fontSize: 11, color: cx.red.main, minHeight: 16 }}>
          {downloadClip.isError ? downloadClip.error.message : ''}
        </Typography>

        <Box
          component="button"
          type="button"
          onClick={handleDownload}
          disabled={!isReady || downloadClip.isPending}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: 32,
            padding: '0 16px',
            flexShrink: 0,
            border: 'none',
            borderRadius: '100px',
            backgroundColor: cx.blue.main,
            color: cx.text.primary,
            fontSize: 12,
            cursor: 'pointer',
            '&:hover': { backgroundColor: cx.blue.hover },
            '&:disabled': { backgroundColor: cx.bg.elevated, color: cx.text.muted, cursor: 'default' },
          }}
        >
          {downloadClip.isPending ? <CircularProgress size={14} color="inherit" /> : <Download size={14} />}
          Download
        </Box>
      </Box>
    </Dialog>
  );
};

export default ClipPlayerDialog;
